import type { ReactNode } from 'react';

/** Line icons (brand: 1.5–1.8 stroke, round caps). Always decorative: text carries the meaning. */
const paths = {
  home: <path d="M4 13h6V4H4zM14 20h6V11h-6zM4 20h6v-3H4zM14 7h6V4h-6z" />,
  queue: <path d="M4 6h16M4 12h10M4 18h6M17 15l3 3-3 3" />,
  places: (
    <>
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  back: <path d="M19 12H5m6-6-6 6 6 6" />,
  check: <path d="m5 12 5 5L20 7" />,
  cross: <path d="M6.5 6.5l11 11m0-11-11 11" />,
  pending: <circle cx="12" cy="12" r="7.5" />,
  oneSource: <circle cx="12" cy="12" r="8" strokeDasharray="3.2 3" />,
  conflict: <path d="M4 8h14l-3.5-3.5M20 16H6l3.5 3.5" />,
  expired: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  none: <path d="M6 12h12" />,
  pilot: <path d="M10 6h10M10 12h10M10 18h10M3.5 6l1.5 1.5L7.5 5M3.5 12l1.5 1.5 2.5-2.5M4 18h3" />,
  external: (
    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  oneOfTwo: (
    <>
      <circle cx="8" cy="12" r="4.5" />
      <circle cx="16" cy="12" r="4.5" strokeDasharray="2.4 2.2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.5v.1" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M10 5.7a9.8 9.8 0 0 1 2-.2c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.6 3.4M6.6 6.6C3.9 8.4 2.5 12 2.5 12S6 18.5 12 18.5a9.5 9.5 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3.5 3.5l17 17" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof paths;

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-none"
    >
      {paths[name]}
    </svg>
  );
}
